import { useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { isActivePage, mergePages } from '@vibesites/runtime';
import {
  EditableToken,
  useCollectionBinding,
  useContentToken,
} from '@vibesites/runtime/react';
import pagesData from '../vibesites-pages.json';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

export function SitemapContent() {
  const heading = useContentToken('sitemapcontent.heading', { defaultValue: 'Sitemap' });
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const pages = useMemo(
    () =>
      // Use the merged site pages (same source as routing) so CMS visibility
      // matches Header navigation — parent theme pages.json alone does not.
      mergePages(pagesData as any)
        .filter((page) => {
          if (!isActivePage(page)) return false;
          // Same "show in navigation" gate as Header.buildNavLinks
          if (page.visible === false) return false;
          if (page.template) return false;
          if (page.alias === 'sitemap' || page.path.includes('{{')) return false;
          if (page.alias === 'virtual-tour' && !hasVirtualTours) return false;
          return true;
        })
        .sort((a, b) => a.position - b.position),
    [hasVirtualTours],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="sitemapcontent.heading"
          as="h1"
          className="font-heading text-3xl font-normal text-black mb-10 text-center"
        >
          {heading}
        </EditableToken>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:text-black hover:border-black transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Sitemap',
    },
  },
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SitemapContent;
